import React from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X, AlertTriangle } from 'lucide-react';

interface DeviationLine {
  pListLineNo: number;
  itemNo: string;
  itemDescription: string;
  qtyOrdered: number;
  qtyPicked: number;
  deviation: number;
}

interface QuantityDeviationModalProps {
  isOpen: boolean;
  deviations: DeviationLine[];
  onConfirm: () => void;
  onCancel: () => void;
}

export const QuantityDeviationModal: React.FC<QuantityDeviationModalProps> = ({
  isOpen,
  deviations,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-3xl sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-orange-50 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <AlertTriangle size={20} className="text-orange-600 sm:w-6 sm:h-6" />
            <h3 className="text-lg sm:text-xl font-semibold text-orange-900">{t('warehouse.modules.picking.deviation.title')}</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
          <p className="text-gray-700 mb-4 text-sm sm:text-base">
            {t('warehouse.modules.picking.deviation.intro')}
          </p>

          <div className="max-h-96 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnLine')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnItemNo')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnDescription')}</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnOrdered')}</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnPicked')}</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">{t('warehouse.modules.picking.deviation.columnDeviation')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deviations.map((deviation) => (
                  <tr key={deviation.pListLineNo} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">#{deviation.pListLineNo}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{deviation.itemNo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{deviation.itemDescription}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{deviation.qtyOrdered}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{deviation.qtyPicked}</td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      deviation.deviation > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {deviation.deviation > 0 ? '+' : ''}{deviation.deviation}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>{t('warehouse.modules.picking.deviation.warningLabel')}</strong> {t('warehouse.modules.picking.deviation.warningText')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-6 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2 sm:order-1"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-3 sm:py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors min-h-[44px] order-1 sm:order-2"
          >
            {t('warehouse.modules.picking.deviation.completeWithDeviation')}
          </button>
        </div>
      </div>
    </div>
  );
};
